import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle, Key } from 'lucide-react';

/**
 * Formulario de recuperación de contraseña
 * Se conecta con la API /api/recuperar-password.php
 */
const FormularioRecuperarPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, ingrese un correo electrónico válido');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch('/api/recuperar-password.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.respuesta === 'Ok') {
        setSubmitStatus('success');
        setSuccessMessage(data.mensaje || 'Se ha enviado un correo con sus credenciales de acceso');
        setEmail(''); // Limpiar el campo
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.mensaje_error || 'Usuario no encontrado o error al procesar la solicitud');
      }
    } catch (error) {
      console.error('Error en recuperación:', error);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#e87729]">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <Key className="w-7 h-7 text-[#e87729]" />
        Recuperar Contraseña
      </h3>

      <p className="text-gray-600 mb-6">
        Ingrese su correo electrónico registrado y le enviaremos sus credenciales de acceso.
      </p>

      {/* Mensaje de éxito */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-lg flex items-start gap-3 mb-6">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">¡Solicitud procesada!</p>
            <p className="text-sm mt-1">{successMessage}</p>
            <p className="text-sm mt-2 text-green-700">
              Revise su correo electrónico (incluya la carpeta de spam).
            </p>
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-lg flex items-start gap-3 mb-6">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Error</p>
            <p className="text-sm mt-1">{errorMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Correo electrónico <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e87729] focus:outline-none transition-colors disabled:bg-gray-100"
              placeholder="correo@ejemplo.com"
            />
          </div>
        </div>

        {/* Nota informativa */}
        <div className="bg-orange-50 border-l-4 border-[#e87729] p-4 rounded-r-lg">
          <p className="text-sm text-orange-800">
            <strong>Nota:</strong> Si no recibe el correo en los próximos minutos, verifique su carpeta de spam o correo no deseado.
          </p>
        </div>

        {/* Botón de envío */}
        <div className="flex justify-center pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-gradient-to-r from-[#e87729] to-[#d66820] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Mail className="w-6 h-6" />
                Recuperar Contraseña
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioRecuperarPassword;
